import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { api, formatDate, type Payment, type PendingPayment, type Enrollment, type Course, type CourseEdition } from "@/lib/api";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import PaymentModal from "@/components/feature/PaymentModal";
import ListingFilters from "@/components/feature/ListingFilters";
import { emptyListingFilters, matchesMonth, type ListingFilterState } from "@/lib/listingFilters";

export default function PagosPage() {
  const [filters, setFilters] = useState<ListingFilterState>(emptyListingFilters);
  const [statusFilter, setStatusFilter] = useState("");
  const [payments, setPayments] = useState<Payment[]>([]);
  const [pendingBalances, setPendingBalances] = useState<PendingPayment[]>([]);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [editions, setEditions] = useState<CourseEdition[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [selectedPending, setSelectedPending] = useState<PendingPayment | null>(null);

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [p, pending, e, c, eds] = await Promise.all([
        api.getPayments(),
        api.getPendingPayments(),
        api.getEnrollments(),
        api.getCourses(),
        api.getEditions(),
      ]);
      setPayments(p);
      setPendingBalances(pending);
      setEnrollments(e);
      setCourses(Array.from(new Map([...c, ...eds.map((ed) => ed.course).filter(Boolean) as Course[]].map((course) => [course.id, course])).values()));
      setEditions(eds);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Error al cargar datos");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const coursesById = useMemo(() => new Map(courses.map((c) => [c.id, c])), [courses]);
  const editionsById = useMemo(() => new Map(editions.map((e) => [e.id, e])), [editions]);

  const matchCourseEditionCategory = (
    courseId: string | null | undefined,
    editionId: string | null | undefined,
    categoryFromRelation?: string | null
  ) => {
    if (filters.courseId && courseId !== filters.courseId) return false;
    if (filters.editionId && editionId !== filters.editionId) return false;
    if (filters.category) {
      const cat =
        categoryFromRelation ??
        (editionId ? editionsById.get(editionId)?.course?.category : undefined) ??
        (courseId ? coursesById.get(courseId)?.category : undefined);
      if (cat !== filters.category) return false;
    }
    return true;
  };

  const handleDeletePayment = async (id: string) => {
    if (!confirm("¿Estás seguro de eliminar este pago? Esta acción no se puede deshacer y solo es válida dentro de las primeras 72 horas.")) return;
    try {
      const res = await api.deletePayment(id);
      toast.success(res.message || "Pago eliminado exitosamente");
      load();
    } catch (error: any) {
      toast.error(error.error || "Error al eliminar el pago");
    }
  };

  const pendingApproval = useMemo(
    () =>
      enrollments.filter((e) => {
        if (e.enrollment_status !== "pendiente") return false;
        const serviceId = e.course_id || e.diplomado_id;
        const editionId = e.edition_id || e.diplomado_edition_id;
        const category = e.edition?.course?.category || e.diplomadoEdition?.diplomado?.category;
        if (!matchCourseEditionCategory(serviceId, editionId, category)) return false;
        if (filters.month) {
          const date =
            e.registration_date ||
            e.edition?.start_date ||
            e.diplomadoEdition?.start_date ||
            editionsById.get(editionId || "")?.start_date;
          if (!matchesMonth(date, filters.month)) return false;
        }
        return true;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enrollments, filters, coursesById, editionsById]
  );

  const pendingPaymentsList = useMemo(
    () =>
      payments
        .filter((p) => {
          if (p.status !== "pendiente") return false;
          const serviceId = p.course_id || p.diplomado_id;
          const editionId = p.edition_id || p.diplomado_edition_id;
          const category = p.edition?.course?.category || p.diplomadoEdition?.diplomado?.category;
          if (!matchCourseEditionCategory(serviceId, editionId, category)) return false;
          if (filters.month && !matchesMonth(p.payment_date, filters.month)) return false;
          return true;
        })
        .map((p) => ({
          ...p,
          student_name: p.student?.full_name || "",
          course_name: p.edition?.course?.name || p.diplomadoEdition?.diplomado?.name || "",
          edition_label: p.edition?.edition_label || p.diplomadoEdition?.edition_label || "",
        })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [payments, filters, coursesById, editionsById]
  );

  const filteredPendingBalances = useMemo(
    () =>
      pendingBalances.filter((p) => {
        if (!matchCourseEditionCategory(p.course_id, p.edition_id, coursesById.get(p.course_id)?.category)) {
          return false;
        }
        if (filters.month) {
          const date =
            p.registration_date ||
            editionsById.get(p.edition_id || "")?.start_date;
          if (!matchesMonth(date, filters.month)) return false;
        }
        return true;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pendingBalances, filters, coursesById, editionsById]
  );

  const enrichedPayments = useMemo(
    () =>
      payments.map((p) => ({
        ...p,
        student_name: p.student?.full_name || "",
          course_name: p.edition?.course?.name || p.diplomadoEdition?.diplomado?.name || "",
          edition_label: p.edition?.edition_label || p.diplomadoEdition?.edition_label || "",
      })),
    [payments]
  );

  const filtered = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return enrichedPayments.filter((p) => {
      if (!matchCourseEditionCategory(p.course_id || p.diplomado_id, p.edition_id || p.diplomado_edition_id, p.edition?.course?.category || p.diplomadoEdition?.diplomado?.category)) return false;
      if (filters.month && !matchesMonth(p.payment_date, filters.month)) return false;
      if (statusFilter && p.status !== statusFilter) return false;
      if (q) {
        const haystack = [
          p.receipt_number,
          p.student_name,
          p.course_name,
          p.reference,
          p.enrollment?.payment_reference,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrichedPayments, filters, statusFilter, coursesById, editionsById]);

  const scopedPayments = useMemo(
    () =>
      payments.filter((p) => {
        if (!matchCourseEditionCategory(p.course_id || p.diplomado_id, p.edition_id || p.diplomado_edition_id, p.edition?.course?.category || p.diplomadoEdition?.diplomado?.category)) return false;
        if (filters.month && !matchesMonth(p.payment_date, filters.month)) return false;
        return true;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [payments, filters, coursesById, editionsById]
  );

  const totalPaid = scopedPayments.filter((p) => p.status === "pagado").reduce((s, p) => s + p.amount, 0);
  const totalPartial = scopedPayments.filter((p) => p.status === "abono_parcial").reduce((s, p) => s + p.amount, 0);
  const totalPending = filteredPendingBalances.reduce((s, p) => s + p.amount_remaining, 0);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);

  const statusLabel = (status: string) => {
    switch (status) {
      case "pagado":
        return <span className="badge-success">Pagado</span>;
      case "cortesia":
        return <span className="badge-info">Cortesía / Exonerado</span>;
      case "abono_parcial":
        return <span className="badge-warning">Abono parcial</span>;
      case "pendiente":
        return <span className="badge-danger">Pendiente</span>;
      case "rechazado":
        return <span className="badge-danger">Rechazado</span>;
      default:
        return <span className="badge-info">{status}</span>;
    }
  };

  const handlePaymentStatus = async (id: string, status: string) => {
    await api.updatePaymentStatus(id, status);
    load();
  };

  const handleMarkCourtesy = async (enrollmentId: string, studentName: string) => {
    if (!confirm(`¿Marcar cortesía/exonerado para ${studentName}?\n\nQuedará liquidado, pero ese monto NO contará como ingreso real.`)) return;
    try {
      await api.markEnrollmentCourtesy(enrollmentId);
      load();
    } catch (err: any) {
      alert(err.message || "No se pudo marcar cortesía");
    }
  };

  const handleAccept = async (id: string) => {
    await api.acceptEnrollment(id);
    load();
  };

  const handleReject = async (id: string) => {
    if (!confirm("¿Rechazar esta inscripción?")) return;
    await api.rejectEnrollment(id);
    load();
  };

  const proofUrl = (url?: string | null) => {
    if (!url) return null;
    return url.startsWith("/") ? `${window.location.origin}${url}` : url;
  };

  const handleExportExcel = () => {
    const data = filtered.map((p) => ({
      "Recibo": p.receipt_number,
      "Estudiante": p.student_name,
      "Curso": p.course_name,
      "Edición": p.edition_label || "—",
      "Monto": p.amount,
      "Referencia Bancaria": p.reference || "—",
      "Ref. Inscripción": p.enrollment?.payment_reference || "—",
      "Estado": p.status,
      "Fecha Pago": p.payment_date ? formatDate(p.payment_date) : "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Pagos");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" });
    saveAs(blob, `pagos_ksm_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  return (
    <div className="space-y-6 animate-[fadeIn_0.3s_ease-out]">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">Pagos e inscripciones</h1>
          <p className="text-sm text-[var(--color-text-muted)] mt-1">
            Saldos pendientes por curso, referencias de pago y recibos
          </p>
        </div>
        <div className="flex items-center gap-4 flex-wrap justify-end">
          <span className="badge-accent">{filtered.length} movimientos</span>
          <button onClick={handleExportExcel} className="btn-secondary flex items-center gap-2">
            <i className="ri-file-excel-2-line"></i> Exportar a Excel
          </button>
        </div>
      </div>

      {!loading && (
        <ListingFilters
          title="Filtros de pagos"
          filters={filters}
          onChange={setFilters}
          courses={courses}
          editions={editions}
          searchPlaceholder="Recibo, referencia, estudiante o curso..."
          resultCount={filtered.length}
          totalCount={payments.length}
          hint="Mes filtra por fecha de pago (movimientos) o registro/inicio de edición (saldos e inscripciones)."
          extraActive={Boolean(statusFilter)}
          onClearExtras={() => setStatusFilter("")}
          extras={
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mb-1 block">
                Estado
              </label>
              <select
                className="input-field"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                aria-label="Filtrar por estado"
              >
                <option value="">Todos los estados</option>
                <option value="pagado">Pagado</option>
                <option value="abono_parcial">Abono parcial</option>
                <option value="pendiente">Pendiente</option>
                <option value="rechazado">Rechazado</option>
              </select>
            </div>
          }
        />
      )}

      {loadError && (
        <div className="card border-l-4 border-l-red-500 text-sm text-red-400">{loadError}</div>
      )}

      {pendingApproval.length > 0 && (
        <div className="card border-l-4 border-l-[var(--color-accent-oil)]">
          <h2 className="font-semibold mb-4 flex items-center gap-2">
            <i className="ri-user-add-line text-[var(--color-accent-oil)]"></i>
            Inscripciones por aprobar ({pendingApproval.length})
          </h2>
          <div className="space-y-3">
            {pendingApproval.map((e) => (
              <div key={e.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-[var(--color-bg-tertiary)] rounded-xl">
                <div>
                  <p className="font-medium">{e.student?.full_name}</p>
                  <p className="text-xs text-[var(--color-text-muted)]">
                    {e.edition?.course?.name || e.diplomadoEdition?.diplomado?.name} · {e.edition?.edition_label || e.diplomadoEdition?.edition_label}
                  </p>
                  {e.payment_reference && <p className="text-xs font-mono text-[var(--color-accent-oil)] mt-1">Ref: {e.payment_reference}</p>}
                  <div className="flex gap-2 mt-1">
                    <span className={`text-[10px] px-2 py-0.5 rounded ${e.payment_status === "pagado" ? "badge-success" : "badge-warning"}`}>{e.payment_status}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleAccept(e.id)} className="btn-primary text-xs cursor-pointer"><i className="ri-check-line"></i> Aceptar</button>
                  <button onClick={() => handleReject(e.id)} className="btn-secondary text-xs cursor-pointer">Rechazar</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {pendingPaymentsList.length > 0 && (
        <div className="card border-l-4 border-l-yellow-500">
          <h2 className="font-semibold mb-4 flex items-center gap-2">
            <i className="ri-file-list-3-line text-yellow-500"></i>
            Comprobantes de pago por verificar ({pendingPaymentsList.length})
          </h2>
          <div className="space-y-3">
            {pendingPaymentsList.map((p) => (
              <div key={p.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-[var(--color-bg-tertiary)] rounded-xl">
                <div>
                  <p className="font-medium">{p.student_name}</p>
                  <p className="text-xs text-[var(--color-text-muted)]">{p.course_name} · {p.edition_label}</p>
                  <div className="flex items-center gap-3 mt-1.5">
                    <span className="font-semibold text-[var(--color-text-primary)]">{formatCurrency(p.amount)}</span>
                    <span className="text-xs font-mono text-[var(--color-accent-oil)]">
                      Ref: {p.reference || p.enrollment?.payment_reference || "Sin ref"}
                      {p.payment_method ? ` (${p.payment_method})` : ""}
                    </span>
                    {p.proof_url ? (
                      <a href={proofUrl(p.proof_url) || "#"} target="_blank" rel="noreferrer" className="text-xs text-blue-400 hover:text-blue-300 hover:underline flex items-center gap-1 bg-blue-500/10 px-2 py-1 rounded">
                        <i className="ri-image-line"></i> Ver comprobante
                      </a>
                    ) : (
                      <span className="text-xs text-[var(--color-text-muted)] flex items-center gap-1">
                        <i className="ri-file-forbid-line"></i> Sin foto adjunta
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handlePaymentStatus(p.id, "pagado")} className="btn-primary text-xs cursor-pointer"><i className="ri-check-line"></i> Confirmar pago</button>
                  <button onClick={() => { if (confirm("¿Rechazar este comprobante?")) handlePaymentStatus(p.id, "rechazado"); }} className="btn-secondary text-xs cursor-pointer">Rechazar</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {filteredPendingBalances.length > 0 && (
        <div className="card border-l-4 border-l-red-500">
          <h2 className="font-semibold text-[var(--color-text-primary)] mb-4 flex items-center gap-2">
            <i className="ri-money-dollar-circle-line text-red-400"></i>
            Saldos pendientes por curso ({filteredPendingBalances.length})
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
                  <th className="pb-3 pr-4">Estudiante</th>
                  <th className="pb-3 pr-4">Curso / Edición</th>
                  <th className="pb-3 pr-4">Referencia</th>
                  <th className="pb-3 pr-4">Precio</th>
                  <th className="pb-3 pr-4">Pagado</th>
                  <th className="pb-3 pr-4">Pendiente</th>
                  <th className="pb-3 pr-4">Estado</th>
                  <th className="pb-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredPendingBalances.map((p) => (
                  <tr key={p.enrollment_id} className="border-b border-[var(--color-border)]/50">
                    <td className="py-3 pr-4">
                      <p className="font-medium">{p.student.full_name}</p>
                      <p className="text-xs text-[var(--color-text-muted)]">{p.student.email}</p>
                    </td>
                    <td className="py-3 pr-4">
                      <p>{p.course_name}</p>
                      <p className="text-xs text-[var(--color-text-muted)]">{p.edition_label || "—"}</p>
                    </td>
                    <td className="py-3 pr-4 font-mono text-xs text-[var(--color-accent-oil)]">
                      {p.payment_reference || "—"}
                    </td>
                    <td className="py-3 pr-4">{formatCurrency(p.course_price)}</td>
                    <td className="py-3 pr-4 text-green-400">{formatCurrency(p.amount_paid)}</td>
                    <td className="py-3 pr-4 font-semibold text-red-400">{formatCurrency(p.amount_remaining)}</td>
                    <td className="py-3 pr-4">{statusLabel(p.payment_status)}</td>
                    <td className="py-3 flex gap-2">
                      <button
                        onClick={() => setSelectedPending(p)}
                        className="btn-primary text-xs py-1 px-3 cursor-pointer"
                      >
                        Abonar
                      </button>
                      <button
                        onClick={() => handleMarkCourtesy(p.enrollment_id, p.student.full_name)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-[var(--color-accent-oil)]/40 text-[var(--color-accent-oil)] bg-[var(--color-accent-oil)]/10 hover:bg-[var(--color-accent-oil)]/20 transition-colors cursor-pointer"
                        title="Exonerar pago por cortesía (no cuenta como ingreso)"
                      >
                        <i className="ri-gift-2-line text-sm"></i>
                        Cortesía
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-[var(--color-text-muted)] mt-3">
            Los certificados no se emiten mientras exista saldo pendiente en la inscripción de ese curso.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card border-l-4 border-l-green-500">
          <p className="text-xs text-[var(--color-text-muted)] uppercase font-medium">Cobrado (pagado)</p>
          <p className="text-2xl font-bold text-green-400 mt-1">{formatCurrency(totalPaid)}</p>
        </div>
        <div className="card border-l-4 border-l-yellow-500">
          <p className="text-xs text-[var(--color-text-muted)] uppercase font-medium">Abonos parciales</p>
          <p className="text-2xl font-bold text-yellow-400 mt-1">{formatCurrency(totalPartial)}</p>
        </div>
        <div className="card border-l-4 border-l-red-500">
          <p className="text-xs text-[var(--color-text-muted)] uppercase font-medium">Por cobrar (cursos)</p>
          <p className="text-2xl font-bold text-red-400 mt-1">{formatCurrency(totalPending)}</p>
        </div>
      </div>

      <div className="card">
        <h2 className="font-semibold text-[var(--color-text-primary)] mb-4 flex items-center gap-2">
          <i className="ri-history-line text-[var(--color-accent)]"></i>
          Historial General de Pagos
        </h2>

        {loading ? (
          <div className="text-center py-8 text-[var(--color-text-muted)]">
            <i className="ri-loader-4-line animate-spin text-2xl"></i>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
                  <th className="pb-3 pr-4">Recibo</th>
                  <th className="pb-3 pr-4">Estudiante</th>
                  <th className="pb-3 pr-4">Curso</th>
                  <th className="pb-3 pr-4">Monto</th>
                  <th className="pb-3 pr-4">Referencia</th>
                  <th className="pb-3 pr-4">Ref. inscripción</th>
                  <th className="pb-3 pr-4">Estado</th>
                  <th className="pb-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} className="border-b border-[var(--color-border)]/50">
                    <td className="py-3 pr-4 font-mono text-xs">{p.receipt_number}</td>
                    <td className="py-3 pr-4">{p.student_name}</td>
                    <td className="py-3 pr-4">
                      <p>{p.course_name}</p>
                      {p.edition_label && (
                        <p className="text-xs text-[var(--color-text-muted)]">{p.edition_label}</p>
                      )}
                    </td>
                    <td className="py-3 pr-4 font-semibold">{formatCurrency(p.amount)}</td>
                    <td className="py-3 pr-4 text-xs font-mono">{p.reference || "—"}</td>
                    <td className="py-3 pr-4 text-xs font-mono text-[var(--color-accent-oil)]">
                      {p.enrollment?.payment_reference || "—"}
                    </td>
                    <td className="py-3 pr-4">{statusLabel(p.status)}</td>
                    <td className="py-3">
                      <div className="flex gap-2 flex-wrap">
                        {p.proof_url && (
                          <a
                            href={proofUrl(p.proof_url) || "#"}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-[var(--color-accent)] hover:underline"
                          >
                            Comprobante
                          </a>
                        )}
                        {p.status !== "pagado" && p.status !== "rechazado" && (
                          <>
                            <button
                              onClick={() => handlePaymentStatus(p.id, "pagado")}
                              className="text-xs text-green-400 hover:underline cursor-pointer"
                            >
                              Confirmar
                            </button>
                            <button
                              onClick={() => { if (confirm("¿Rechazar este comprobante?")) handlePaymentStatus(p.id, "rechazado"); }}
                              className="text-xs text-red-400 hover:underline cursor-pointer"
                            >
                              Rechazar
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDeletePayment(p.id)}
                          className="text-xs text-red-500 hover:text-red-400 cursor-pointer ml-1"
                          title="Eliminar Pago (solo <72h)"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedPending && (
        <PaymentModal
          isOpen={true}
          onClose={() => setSelectedPending(null)}
          enrollmentId={selectedPending.enrollment_id}
          courseName={selectedPending.course_name}
          editionLabel={selectedPending.edition_label}
          coursePrice={selectedPending.course_price}
          amountPaid={selectedPending.amount_paid}
          paymentReference={selectedPending.payment_reference}
          onSuccess={load}
        />
      )}
    </div>
  );
}
